import { useEffect, useRef } from 'react';

interface NeuralNetworkOverlayProps {
  opacity?: number;
}

export default function NeuralNetworkOverlay({ opacity = 0.8 }: NeuralNetworkOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let nodes: Node[] = [];
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();

    // Parse CSS color string (hex or rgb/rgba) to [r,g,b]
    const parseCssColorToRgb = (input: string): [number, number, number] => {
      let c = input.trim();
      if (!c) return [10, 116, 255];
      const rgbMatch = c.match(/^rgba?\(([^)]+)\)$/i);
      if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map(v => parseFloat(v.trim()));
        return [
          Math.max(0, Math.min(255, parts[0] || 0)),
          Math.max(0, Math.min(255, parts[1] || 0)),
          Math.max(0, Math.min(255, parts[2] || 0)),
        ];
      }
      if (c.startsWith('#')) c = c.slice(1);
      if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
      if (c.length !== 6) return [10, 116, 255];
      const r = parseInt(c.slice(0,2), 16);
      const g = parseInt(c.slice(2,4), 16);
      const b = parseInt(c.slice(4,6), 16);
      return [r,g,b];
    };

    // Accent rgba base string updated each frame
    let colorBase = 'rgba(10, 116, 255,';

    class Node {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      pulse: number;
      pulseSpeed: number;
      color: string;

      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.3;
        this.vy = (Math.random() - 0.5) * 0.3;
        this.radius = Math.random() * 3 + 2;
        this.pulse = Math.random() * Math.PI * 2;
        this.pulseSpeed = Math.random() * 0.05 + 0.02;
        this.color = colorBase;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.pulse += this.pulseSpeed;

        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;

        this.x = Math.max(0, Math.min(canvas.width, this.x));
        this.y = Math.max(0, Math.min(canvas.height, this.y));
      }

      draw() {
        const pulseSize = Math.sin(this.pulse) * 2 + this.radius;
        
        const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, pulseSize * 2);
        gradient.addColorStop(0, `${colorBase} 0.6)`);
        gradient.addColorStop(0.5, `${colorBase} 0.3)`);
        gradient.addColorStop(1, `${colorBase} 0)`);
        
        ctx.beginPath();
        ctx.arc(this.x, this.y, pulseSize * 2, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
        
        ctx.beginPath();
        ctx.arc(this.x, this.y, pulseSize, 0, Math.PI * 2);
        ctx.fillStyle = `${colorBase} 0.9)`;
        ctx.fill();
      }
    }

    const createNodes = () => {
      nodes = [];
      const nodeCount = 60;
      for (let i = 0; i < nodeCount; i++) {
        nodes.push(new Node());
      }
    };

    createNodes();

    const drawConnections = () => {
      const maxDistance = 150;
      
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < maxDistance) {
            const opacity = (1 - distance / maxDistance) * 0.4;
            
            const pulsePosition = (time * 0.5 + i + j) % 1;
            const pulseX = nodes[i].x + (nodes[j].x - nodes[i].x) * pulsePosition;
            const pulseY = nodes[i].y + (nodes[j].y - nodes[i].y) * pulsePosition;
            
            const gradient = ctx.createLinearGradient(nodes[i].x, nodes[i].y, nodes[j].x, nodes[j].y);
            // Use the same accent color across the gradient for a clean accent line
            const accentOpacityMid = Math.min(1, opacity * 1.5);
            gradient.addColorStop(0, `${colorBase} ${opacity})`);
            gradient.addColorStop(0.5, `${colorBase} ${accentOpacityMid})`);
            gradient.addColorStop(1, `${colorBase} ${opacity})`);
            
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = gradient;
            ctx.lineWidth = 1.5;
            ctx.stroke();
            
            ctx.beginPath();
            ctx.arc(pulseX, pulseY, 3, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(255, 255, 255, ${opacity * 2})`;
            ctx.fill();
          }
        }
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 0.01;

      // Read header accent each frame to respond to theme/accent changes
      const accent = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-header-accent')
        .trim() || '#0A74FF';
      const [r,g,b] = parseCssColorToRgb(accent);
      colorBase = `rgba(${r}, ${g}, ${b},`;

      drawConnections();

      nodes.forEach(node => {
        node.update();
        node.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      resizeCanvas();
      createNodes();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [opacity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1,
        opacity: opacity
      }}
    />
  );
}
